import { defineStore } from 'pinia';
import { fetchWrapper } from '@/helpers';
import { useAlertStore } from '..';
import { updateStatistics } from './statistics.store';

const baseUrl = `${process.env.VUE_APP_VUE_API_URL}`;

export const useAdminProxyStore = defineStore('admin-proxy', {
    state: () => ({
        xProxies: [],
        smartProxies: [],
        iProxies: [],
        serverNames: [],
        loading: false
    }),
    actions: {
        async loadProxies(serverName) {
            const alertStore = useAlertStore();
            try {
                this.loading = true;
                const res = await fetchWrapper.post(`${baseUrl}/admin/proxy/load`, { serverName });
                this.xProxies = res.totalProxies.xProxyResult
                this.smartProxies = res.totalProxies.proxySmartResult
                this.iProxies = res.totalProxies.iProxyResult
                updateStatistics()
                this.loading = false;
            } catch (error) {
                this.loading = false;
                alertStore.error(error);
            }
        },
        async loadDisProxies(serverName) {
            const alertStore = useAlertStore();
            try {
                this.loading = true;
                const res = await fetchWrapper.post(`${baseUrl}/admin/proxy/disload`, { serverName });
                this.xProxies = res.totalProxies.xProxyResult
                this.smartProxies = res.totalProxies.proxySmartResult
                this.iProxies = res.totalProxies.iProxyResult || []
                updateStatistics()
                this.loading = false;
            } catch (error) {
                this.loading = false;
                alertStore.error(error);
            }
        },
        async loadServerNames() {
            const alertStore = useAlertStore();
            try {
                const res = await fetchWrapper.get(`${baseUrl}/admin/proxy/load-server-names`,);
                this.serverNames = res.serverNames
            } catch (error) {
                alertStore.error(error);
            }
        },
        async loadPositionList(serverName) {
            const alertStore = useAlertStore();
            try {
                const res = await fetchWrapper.post(`${baseUrl}/admin/proxy/load-position-list`, { serverName });
                return res.positionList
            } catch (error) {
                alertStore.error(error);
            }
        },
        async load_PS_AvailableModemList(serverName) {
            const alertStore = useAlertStore();
            try {
                const res = await fetchWrapper.post(`${baseUrl}/admin/proxy/load-proxysmart-modem-list`, { serverName });
                return {modemList: res.modemList, httpList: res.httpList, socketList: res.socketList}
            } catch (error) {
                alertStore.error(error);
            }
        },
        async createNewProxy(serverName, proxyRequest) {
            const alertStore = useAlertStore();
            try {
                await fetchWrapper.post(`${baseUrl}/admin/proxy/create`, { serverName, proxyRequest });
                updateStatistics()
                alertStore.success("New successfully added")

            } catch (error) {
                alertStore.error(error);
            }
        },
        async createIProxyAccess(serverName, accessRequest) {
            const alertStore = useAlertStore();
            try {
                await fetchWrapper.post(`${baseUrl}/admin/iproxy/create-access`, { serverName, accessRequest });
                updateStatistics();
                alertStore.success("iProxy access created");
            } catch (error) {
                alertStore.error(error);
            }
        },
        async updateProxy(uuid, modem_type, modem_id, proxyRequest) {
            const alertStore = useAlertStore();
            try {
                await fetchWrapper.post(`${baseUrl}/admin/proxy/update`, { uuid, modem_type, modem_id, proxyRequest });
                if(modem_type === 'X_PROXY') {
                    this.xProxies = this.xProxies.map(xproxy => {
                        if(xproxy.uuid === uuid) {
                            xproxy = {
                                ...xproxy,
                                ...proxyRequest,
                                uploadLimitType: proxyRequest.uploadLimitType ? 'limited' : 'unlimited',
                                downloadLimitType: proxyRequest.downloadLimitType ? 'limited' : 'unlimited',
                                upDownLimitType: proxyRequest.upDownLimitType ? 'limited' : 'unlimited',
                            }
                        }
                        return xproxy;
                    })
                } else if (modem_type === 'PROXYSMART') {
                    this.smartProxies = this.smartProxies.map(proxysmart => {
                        if(proxysmart.uuid === uuid) {
                            proxysmart = {
                                ...proxysmart,
                                ...proxyRequest,
                                bandLimitOutType: proxyRequest.bandLimitOutType ? 'limited' : 'unlimited',
                                bandLimitInType: proxyRequest.bandLimitInType ? 'limited' : 'unlimited',
                                quotaType: proxyRequest.quotaType ? 'limited' : 'unlimited',
                            }
                        }
                        return proxysmart;
                    })
                } else if (modem_type === 'IPROXY') {
                    this.iProxies = this.iProxies.map(proxy => {
                        if (proxy.uuid === uuid) {
                            return {
                                ...proxy,
                                auth_user: proxyRequest.userAuthentication || null,
                                auth_ips: proxyRequest.ipAuthentication || null,
                            };
                        }
                        return proxy;
                    });
                }
                updateStatistics()
                alertStore.success("Proxy updated")

            } catch (error) {
                alertStore.error(error);
            }
        },
        async updateAction(uuid) {
            const alertStore = useAlertStore();
            try {
                // Determine current state BEFORE toggling (for correct messaging)
                let currentActive = null;
                const fromX = this.xProxies.find(p => p.uuid === uuid);
                const fromSmart = this.smartProxies.find(p => p.uuid === uuid);
                const fromIproxy = this.iProxies.find(p => p.uuid === uuid);

                if (fromX) currentActive = fromX.active || 'active';
                else if (fromSmart) currentActive = fromSmart.active || 'active';
                else if (fromIproxy) currentActive = fromIproxy.active || 'active';

                const res = await fetchWrapper.post(`${baseUrl}/admin/proxy/update-active`, {uuid});
                const item = res.item || {};
                const typeFromResponse = item.modem_type;

                // Fallback: infer type from current lists if modem_type is missing
                const inferredType =
                    typeFromResponse ||
                    (this.xProxies.find(p => p.uuid === item.uuid) && 'X_PROXY') ||
                    (this.smartProxies.find(p => p.uuid === item.uuid) && 'PROXYSMART') ||
                    (this.iProxies.find(p => p.uuid === item.uuid) && 'IPROXY');

                if(inferredType === "X_PROXY") {
                    this.xProxies = this.xProxies.map(xproxy => {
                        if(item.uuid === xproxy.uuid) {
                            xproxy.active = item.active;
                        }
                        return xproxy;
                    });
                } else if (inferredType === "PROXYSMART") {
                    this.smartProxies = this.smartProxies.map(proxysmart => {
                        if(item.uuid === proxysmart.uuid) {
                            proxysmart.active = item.active;
                        }
                        return proxysmart;
                    });
                } else if (inferredType === "IPROXY") {
                    this.iProxies = this.iProxies.map(proxy => {
                        if (item.uuid === proxy.uuid) {
                            proxy.active = item.active;
                        }
                        return proxy;
                    });
                }

                // Message based on PREVIOUS state: if it was active, we just paused it
                if(currentActive === 'active') {
                    alertStore.success("Proxy paused")
                } else {
                    alertStore.success("Proxy resumed")
                }
            } catch(error) {
                alertStore.error(error)
            }

        },
        async deleteProxy(uuids) {
            const alertStore = useAlertStore();
            try {
                const promises = uuids.map(uuid => 
                    fetchWrapper.post(`${baseUrl}/admin/proxy/delete`, { uuids: [uuid] })
                );
                const results = await Promise.allSettled(promises);
                const successes = results.filter(r => r.status === 'fulfilled').length;
                const failures = results.filter(r => r.status === 'rejected');
                
                updateStatistics()
                
                if (failures.length === 0) {
                    alertStore.success(`Deleted ${successes} proxies`);
                } else {
                    console.warn('Some deletes failed:', failures);
                    alertStore.error(`${failures.length} deletes failed, ${successes} succeeded`);
                }
            } catch (error) {
                alertStore.error(error);
            }
        },
        async hasIProxyAccess(serverName) {
            const alertStore = useAlertStore();
            try {
                const res = await fetchWrapper.post(`${baseUrl}/admin/iproxy/access-exists`, { serverName });
                return { exists: !!res.exists, count: res.count || 0 };
            } catch (error) {
                return { exists: false, count: 0 };
            }
        },
    },
});